import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { money, reduceMotion } from './Ring';

/* Bars grow from their last width (kept across tab switches), never from zero. */
const prevW = {};
const bd = (n) => `${n} business day${n === 1 ? '' : 's'} left`;

function Bar({ k, tone, w }) {
  const [width, setWidth] = useState(() => prevW[k] ?? 0);
  useEffect(() => {
    prevW[k] = w;
    if (reduceMotion()) { setWidth(w); return undefined; }
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setWidth(w)));
    return () => cancelAnimationFrame(id);
  }, [k, w]);
  return <div className="gpf-bar"><i className={`t-${tone}`} style={{ width: `${width}%` }} /></div>;
}

/* One focus line: amber (behind pace) / blue (on pace) / green (met). */
function Line({ k, data, title, sub, delay, changed, numNode }) {
  if (!data || !data.budget) return null;
  const frac = data.budget ? data.done / data.budget : 0;
  const tone = data.met ? 'green' : (frac >= (data.elapsed || 0) ? 'blue' : 'amber');
  const barW = Math.min(100, Math.max(0, frac * 100));
  return (
    <li className={`gpf-row t-${tone}${changed ? ' gpf-changed' : ''}`} style={{ animationDelay: `${delay}ms` }} data-testid={`gpf-row-${k}`}>
      <div className="gpf-n" data-testid={`gpf-n-${k}`}>
        {data.met ? <Check strokeWidth={3} aria-label="Budget met" /> : (numNode || <span className="num">{data.need}</span>)}
      </div>
      <div className="gpf-b">
        <b data-testid={`gpf-title-${k}`}>{title}</b>
        <small className="num" data-testid={`gpf-sub-${k}`}>{sub}</small>
        <Bar k={k} tone={tone} w={barW} />
      </div>
    </li>
  );
}

/* "What to focus on" — three live lines telling the team what's still needed to meet budget. */
export default function FocusPanel({ live }) {
  const f = live?.focus;
  const sig = useRef('');
  const [changed, setChanged] = useState({});
  useEffect(() => {
    if (!f) return undefined;
    const next = `${f.approvals?.done}|${f.lodgements?.done}|${f.settlements?.done}`;
    if (sig.current && sig.current !== next) {
      const diff = {};
      if (f.approvals && `${f.approvals.done}` !== sig.current.split('|')[0]) diff.approvals = true;
      if (f.lodgements && `${f.lodgements.done}` !== sig.current.split('|')[1]) diff.lodgements = true;
      if (f.settlements && `${f.settlements.done}` !== sig.current.split('|')[2]) diff.settlements = true;
      setChanged(diff);
      const id = setTimeout(() => setChanged({}), 1800);
      sig.current = next;
      return () => clearTimeout(id);
    }
    sig.current = next;
    return undefined;
  }, [f?.approvals?.done, f?.lodgements?.done, f?.settlements?.done]); // eslint-disable-line

  if (!f) return null;
  const a = f.approvals, l = f.lodgements, s = f.settlements;
  const noTargets = !a?.budget && !l?.budget && !s?.budget;

  const aTitle = a?.met ? 'Approval budget met this week' : `Need ${a?.need} more approval${a?.need === 1 ? '' : 's'} this week to meet budget`;
  const lTitle = l?.met ? 'Lodgement budget met this week' : `Need ${l?.need} more lodgement${l?.need === 1 ? '' : 's'} this week to meet budget`;
  const sTitle = s?.met
    ? 'Settlement budget met this month'
    : (s?.show_dollars
      ? `Need ${money(s?.remaining_value)} more in settlements this month to meet budget`
      : `Need ${s?.need} more settlement${s?.need === 1 ? '' : 's'} this month to meet budget`);

  const aSub = `${a?.done} of ${a?.budget} approved · ${bd(a?.bd_left)}`;
  const lSub = `${l?.done} of ${l?.budget} lodged · ${bd(l?.bd_left)}`;
  const sSub = s?.show_dollars
    ? `${money(s?.settled_value)} of ${money(s?.budget_value, 0)} settled · ${money(s?.remaining_value)} to go · ${bd(s?.bd_left)}`
    : `${money(s?.settled_value)} of ${money(s?.budget_value, 0)} settled · ${money(s?.remaining_value)} to go at about ${money(s?.avg_file)} a file · ${bd(s?.bd_left)}`;
  const sNum = s?.show_dollars ? <span className="num gpf-n-sm">{money(s?.remaining_value)}</span> : undefined;

  return (
    <section className="gpj-panel gpf" data-testid="gpj-focus">
      <div className="gpj-panel-h">
        <h2>What to focus on</h2>
        <p data-testid="gpf-sub">{f.is_current_view ? 'To meet budget' : 'Showing the current week and month'}</p>
      </div>
      {noTargets ? (
        <div className="gpj-empty" data-testid="gpf-empty">Set a monthly settlement target in Set Goals to see what to focus on.</div>
      ) : (
        <ul className="gpf-list" aria-live="polite" data-testid="gpf-list">
          <Line k="approvals" data={a} title={aTitle} sub={aSub} delay={0} changed={changed.approvals} />
          <Line k="lodgements" data={l} title={lTitle} sub={lSub} delay={80} changed={changed.lodgements} />
          <Line k="settlements" data={s} title={sTitle} sub={sSub} delay={160} changed={changed.settlements} numNode={sNum} />
        </ul>
      )}
    </section>
  );
}
